import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Button,
  Checkbox,
  FileButton,
  Group,
  NumberInput,
  Select,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import type { KkdZimmet } from '@isg/validation';
import {
  useCiktiIndir,
  useKkdImzaliNushaIndir,
  useKkdImzaliNushaSil,
  useKkdImzaliNushaYukle,
  useKkdZimmetEkle,
  useKkdZimmetIptal,
  useKkdZimmetleri,
  useLookup,
} from '../api/queries';
import { gunGirdisiDegeri, gunGirdisiIso, tarihGoster, tamSayiGoster } from '../lib/format';
import { useVarsayilanGun } from '../lib/saha-gunu';
import {
  Bolum,
  KapiSebebi,
  Rozet,
  SatirEylemleri,
  SatirSilmeSebebi,
  SolukSatir,
} from '../sozlesme';
import { ANLAM } from '../theme';
import { QueryStatus } from './parts';

/**
 * KKD ZİMMETİ — KİŞİ KARTI BÖLÜMÜ (Kapsam §30/Z7; sözleşme P1-a · P2 · P11).
 *
 * **TURUN TEK CÜMLESİ: KKD ZİMMETİ BİR TESLİM KAYDIDIR — ÖMÜR, STOK VE
 * UYGUNLUK DEĞİL.** Bu bölüm görevlendirmenin KARDEŞİDİR (ızgara dışı, tam
 * genişlik, satır içi ekleme) ve uygunluk paneline GİRMEZ (Z4 — vadesiz
 * kayıt bir şart değildir).
 *
 * ⚠️ **DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN** (`yetkiler.yazabilir` ·
 * `imzaliNusha.silebilir` — §2.40). ⚠️ **D1:** onay kutusunun değeri
 * `setState` güncelleyicisinin İÇİNDE `e.currentTarget`tan OKUNMAZ (tur 8 /
 * F5 · kurul dersi — üç kez tekrarladı).
 * ⚠️ **D9:** imzalı nüshalı satırda iptal eylemi ÇİZİLMEZ (sunucu 409 verir;
 * çalışamayacak kontrol görünmez — §2.40/2).
 * ⚠️ **VERİ KAYNAĞI VAR → bölüm her kişide çizilir**; kaydı olmayan kişide
 * "kayıt yok" satırı (madde 170'in kapanış koşulu).
 */
export function KkdZimmetBolumu({ personId }: { personId: string }): React.JSX.Element {
  const zimmetler = useKkdZimmetleri(personId);
  const kalemler = useLookup('kkd_kalemi');
  const ekle = useKkdZimmetEkle(personId);
  const iptal = useKkdZimmetIptal(personId);
  const nushaYukle = useKkdImzaliNushaYukle(personId);
  const nushaSil = useKkdImzaliNushaSil(personId);
  const nushaIndir = useKkdImzaliNushaIndir(personId);
  const pdf = useCiktiIndir();

  const [kalemId, setKalemId] = useState<string | null>(null);
  const [adet, setAdet] = useState<number | string>(1);
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir.
  const [tarih, setTarih] = useVarsayilanGun();
  const [secili, setSecili] = useState<readonly string[]>([]);
  const [dosya, setDosya] = useState<File | null>(null);
  const [iptalId, setIptalId] = useState<string | null>(null);
  const [gerekce, setGerekce] = useState('');

  const hata = ekle.error ?? iptal.error ?? nushaYukle.error ?? nushaSil.error;
  const yazabilir = zimmetler.data?.yetkiler.yazabilir ?? false;
  const satirlar: readonly KkdZimmet[] = zimmetler.data?.satirlar ?? [];
  // Eylem kolonu: yazabilen (iptal · nüsha) VEYA silinebilir nüshası olan (holding yön.).
  const eylemKolonu =
    yazabilir || satirlar.some((s) => s.imzaliNusha?.silmeKapisi.yazabilir ?? false);
  const dokunuldu = kalemId !== null || adet !== 1;
  // P12 (madde 224) — kaydedilmemiş zimmet satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'zimmet satırı');
  const adetSayi = typeof adet === 'number' ? adet : Number(adet);
  const kaydedilebilir =
    kalemId !== null && Number.isInteger(adetSayi) && adetSayi > 0 && gunGirdisiIso(tarih) !== null;
  // Nüshasız satırlar seçilebilir; nüshalı satıra ikinci nüsha sunucuda 409 — kutu ÇİZİLMEZ.
  const secilebilirIdler = satirlar.filter((s) => s.imzaliNusha === null).map((s) => s.id);
  const seciliGecerli = secili.filter((id) => secilebilirIdler.includes(id));

  function indir(blob: Blob, ad: string): void {
    const url = URL.createObjectURL(blob);
    const bag = document.createElement('a');
    bag.href = url;
    bag.download = ad;
    document.body.appendChild(bag);
    bag.click();
    bag.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Bolum
      baslik="KKD zimmeti"
      aciklama="Teslim kayıtları: kime hangi koruyucu donanım, kaç adet, ne zaman verildi. Yenileme ve stok takibi bu listede yoktur."
      testId="kisi-kkd"
    >
      <Group justify="space-between" mb="xs" wrap="wrap">
        <Button
          size="xs"
          variant="default"
          data-testid="kkd-form-pdf"
          loading={pdf.isPending}
          onClick={() =>
            pdf.mutate({
              yol: `/api/persons/${personId}/ppe-issues/form.pdf`,
              yedekAd: 'kkd-zimmet-formu.pdf',
            })
          }
        >
          Zimmet formu PDF
        </Button>
        {yazabilir && (
          <Group gap="xs" wrap="nowrap">
            <FileButton onChange={setDosya} accept="application/pdf,image/jpeg,image/png">
              {(props) => (
                <Button {...props} size="xs" variant="light" data-testid="kkd-nusha-dosya">
                  {dosya ? dosya.name : '+ İmzalı nüsha seç'}
                </Button>
              )}
            </FileButton>
            <Button
              size="xs"
              data-testid="kkd-nusha-yukle"
              disabled={dosya === null || seciliGecerli.length === 0}
              loading={nushaYukle.isPending}
              onClick={() => {
                if (!dosya) return;
                nushaYukle.mutate(
                  { dosya, issueIds: seciliGecerli },
                  {
                    onSuccess: () => {
                      setDosya(null);
                      setSecili([]);
                    },
                  },
                );
              }}
            >
              Seçili satırlara yükle ({tamSayiGoster(seciliGecerli.length)})
            </Button>
          </Group>
        )}
      </Group>
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kkd-hata" mb="xs">
          {hata.message}
        </Alert>
      )}
      <QueryStatus queries={[zimmetler, kalemler]} />
      {zimmetler.data && (
        <Table striped data-testid="kkd-tablosu">
          <Table.Thead>
            <Table.Tr>
              {yazabilir && <Table.Th w={36} aria-label="Nüsha için seç" />}
              <Table.Th>KKD kalemi</Table.Th>
              <Table.Th w={90}>Adet</Table.Th>
              <Table.Th w={130}>Teslim tarihi</Table.Th>
              <Table.Th>Teslim eden</Table.Th>
              <Table.Th w={220}>İmzalı nüsha</Table.Th>
              {eylemKolonu && <Table.Th w={44} aria-label="Satır eylemi" />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {satirlar.length === 0 && (
              <Table.Tr data-testid="kkd-kayit-yok">
                <Table.Td colSpan={(yazabilir ? 6 : 5) + (eylemKolonu ? 1 : 0)}>
                  <Text size="sm" c="dimmed">
                    KKD zimmet kaydı yok.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {satirlar.map((s) => (
              <Table.Tr key={s.id} data-testid={`kkd-satir-${s.id}`}>
                {yazabilir && (
                  <Table.Td>
                    {s.imzaliNusha === null && (
                      <Checkbox
                        size="xs"
                        aria-label="İmzalı nüsha için seç"
                        data-testid={`kkd-sec-${s.id}`}
                        checked={secili.includes(s.id)}
                        onChange={(e) => {
                          // D1 — değer güncelleyicinin DIŞINDA okunur.
                          const isaretli = e.currentTarget.checked;
                          setSecili((eski) =>
                            isaretli ? [...eski, s.id] : eski.filter((id) => id !== s.id),
                          );
                        }}
                      />
                    )}
                  </Table.Td>
                )}
                <Table.Td>{s.kalemAd}</Table.Td>
                <Table.Td>{tamSayiGoster(s.adet)}</Table.Td>
                <Table.Td>{tarihGoster(s.teslimTarihi)}</Table.Td>
                <Table.Td>{s.teslimEdenEtiket}</Table.Td>
                <Table.Td>
                  {s.imzaliNusha ? (
                    <Group gap={6} wrap="nowrap">
                      <Rozet anlam="olumlu" testId={`kkd-nusha-var-${s.id}`}>
                        İmzalı
                      </Rozet>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        data-testid={`kkd-nusha-indir-${s.id}`}
                        onClick={() =>
                          void nushaIndir(s.id).then(({ blob, dosyaAdi }) =>
                            indir(blob, dosyaAdi ?? s.imzaliNusha?.dosyaAdi ?? 'imzali-nusha'),
                          )
                        }
                      >
                        {s.imzaliNusha.dosyaAdi}
                      </Button>
                    </Group>
                  ) : (
                    <Rozet anlam="dikkat" testId={`kkd-nusha-yok-${s.id}`}>
                      İmzalı nüsha bekliyor
                    </Rozet>
                  )}
                </Table.Td>
                {eylemKolonu && (
                  <Table.Td>
                    <SatirEylemleri
                      testId={`kkd-eylemler-${s.id}`}
                      digerleri={[
                        {
                          etiket: 'Zimmeti iptal et',
                          tehlikeli: true,
                          // D9 — imzalı satırda iptal yolu ÇİZİLMEZ (sunucu 409); iptal yazma kapısıdır.
                          gizli: !yazabilir || s.imzaliNusha !== null,
                          testId: `kkd-iptal-${s.id}`,
                          onClick: () => {
                            setGerekce('');
                            setIptalId(s.id);
                          },
                        },
                        {
                          etiket: 'İmzalı nüshayı sil',
                          tehlikeli: true,
                          // P13-c (madde 227) — nüsha silme kapısı SATIR BAŞINA sunucudan
                          // (uzman + holding yön. → kişi bahçesi → sahiplik).
                          gizli: !(s.imzaliNusha?.silmeKapisi.yazabilir ?? false),
                          testId: `kkd-nusha-sil-${s.id}`,
                          onClick: () => nushaSil.mutate({ id: s.id }),
                        },
                      ]}
                    />
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
            {iptalId && (
              <Table.Tr data-testid="kkd-iptal-satiri">
                <Table.Td colSpan={7}>
                  <Group gap="xs" wrap="nowrap">
                    <TextInput
                      size="xs"
                      placeholder="İptal gerekçesi (zorunlu)"
                      aria-label="İptal gerekçesi"
                      value={gerekce}
                      onChange={(e) => setGerekce(e.currentTarget.value)}
                      data-testid="kkd-iptal-gerekce"
                      style={{ flex: 1 }}
                    />
                    <Button
                      size="xs"
                      color={ANLAM.engel}
                      data-testid="kkd-iptal-onayla"
                      disabled={gerekce.trim().length < 3}
                      loading={iptal.isPending}
                      onClick={() =>
                        iptal.mutate(
                          { id: iptalId, gerekce: gerekce.trim() },
                          { onSuccess: () => setIptalId(null) },
                        )
                      }
                    >
                      İptal et
                    </Button>
                    <Button size="xs" variant="subtle" onClick={() => setIptalId(null)}>
                      Vazgeç
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            )}
            {yazabilir && (
              <SolukSatir
                testId="kkd-soluk"
                dokunuldu={dokunuldu}
                kaydedilebilir={kaydedilebilir}
                kaydediliyor={ekle.isPending}
                kolonSayisi={6}
                onKaydet={() => {
                  if (!kaydedilebilir || kalemId === null) return;
                  const iso = gunGirdisiIso(tarih);
                  if (!iso) return;
                  // Gövde GÜN taşır (`YYYY-AA-GG`, G11) — damga değil; dönüşüm tek kaynaktan.
                  ekle.mutate(
                    { kalemId, adet: adetSayi, teslimTarihi: gunGirdisiDegeri(iso) },
                    {
                      onSuccess: () => {
                        setKalemId(null);
                        setAdet(1);
                      },
                    },
                  );
                }}
              >
                <Table.Td />
                <Table.Td>
                  <Select
                    size="xs"
                    searchable
                    // Kart içeriği kırpar — açılır liste portala (görevlendirme dersi).
                    comboboxProps={{ withinPortal: true }}
                    placeholder="KKD kalemi seç"
                    aria-label="KKD kalemi"
                    data-testid="kkd-soluk-kalem"
                    value={kalemId}
                    onChange={setKalemId}
                    data={(kalemler.data ?? [])
                      .filter((k) => !k.gizli)
                      .map((k) => ({ value: k.id, label: k.ad }))}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    size="xs"
                    min={1}
                    max={999}
                    allowDecimal={false}
                    aria-label="Adet"
                    data-testid="kkd-soluk-adet"
                    value={adet}
                    onChange={setAdet}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    type="date"
                    aria-label="Teslim tarihi"
                    data-testid="kkd-soluk-tarih"
                    value={tarih}
                    onChange={(e) => setTarih(e.currentTarget.value)}
                  />
                </Table.Td>
                <Table.Td colSpan={2}>
                  <Text size="xs" c="dimmed">
                    Teslim eden: siz
                  </Text>
                </Table.Td>
              </SolukSatir>
            )}
          </Table.Tbody>
        </Table>
      )}
      {/* ✏️ GEZİNTİ ARTÇISI 2 / B3 — YAZAMAYAN ŞAPKADA BÖLÜM SALT OKUNUR +
          SEBEP. Gezintide bölüm boş görünüyor, kullanıcı EKSİKLİK sanıyordu
          (yetki kapısı doğru çalışıyordu — Z5). Sebep SUNUCUDAN (bayrakla aynı
          yüklem), şapka adı kapının KENDİ sabitinden. */}
      {zimmetler.data && (
        <KapiSebebi
          durum={zimmetler.data.yetkiler.yazmaKapisi}
          nesne="KKD zimmetini"
          fiil="açar"
          testId="kkd-yazamama-sebebi"
        />
      )}
      {/* P13-c — nüsha silme sebebi bölüm başına BİR KEZ (ilk kapalı satır). */}
      {zimmetler.data && (
        <SatirSilmeSebebi
          kapilar={satirlar.flatMap((s) => (s.imzaliNusha ? [s.imzaliNusha.silmeKapisi] : []))}
          nesne="İmzalı nüshayı"
          fiil="siler"
          testId="kkd-nusha-silme-sebebi"
        />
      )}
    </Bolum>
  );
}
